/**
 * Kundenverwaltung - PR-P0-1: Vollständiges CRUD + Import
 * 
 * Funktionen:
 * - Liste mit Suche (Name, Kürzel, Extern-ID)
 * - Anlegen: Dialog mit Name, Kürzel, Typ, Extern-ID
 * - Bearbeiten: Klick auf Zeile öffnet Edit-Dialog
 * - Import: CSV/Excel Import mit Lieferadr.-Nr. als Upsert-Key
 * - Löschen: Soft-Delete mit Confirm-Dialog
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus, RefreshCw, Search, X, Upload, Users, Check, UserMinus } from 'lucide-react';
import { CsvDropzone, ImportField, FieldMapping, ImportResult } from '../../components/CsvDropzone';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../App';

interface Customer {
  id: string;
  name: string;
  shortName: string | null;
  externalDeliveryId: string | null;
  externalCustomerNo: string | null;
  type: 'REGULAR' | 'COLLECTIVE' | 'RESIDENT';
  isActive: boolean;
  customerGroupId: string | null;
  customerGroup: { id: string; name: string } | null;
}

type CustomerType = 'REGULAR' | 'COLLECTIVE' | 'RESIDENT';

const TYPE_LABELS: Record<CustomerType, string> = {
  REGULAR: 'Stammkunde',
  COLLECTIVE: 'Sammelkunde',
  RESIDENT: 'Heimbewohner',
};

const TYPE_OPTIONS: { value: CustomerType; label: string }[] = [
  { value: 'REGULAR', label: 'Stammkunde' },
  { value: 'COLLECTIVE', label: 'Sammelkunde' },
  { value: 'RESIDENT', label: 'Heimbewohner' },
];

interface FormData {
  name: string;
  shortName: string;
  externalDeliveryId: string;
  externalCustomerNo: string;
  type: CustomerType;
}

const INITIAL_FORM: FormData = {
  name: '',
  shortName: '',
  externalDeliveryId: '',
  externalCustomerNo: '',
  type: 'REGULAR',
};

export default function CustomersPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Remove from Group State
  const [removingFromGroup, setRemovingFromGroup] = useState<string | null>(null);
  
  // Import Dialog State
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  
  // Bulk Selection State
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [customerNoPrefix, setCustomerNoPrefix] = useState('');
  // Initialisiere filterGroupId aus URL-Parameter 'group'
  const [filterGroupId, setFilterGroupId] = useState<string>(() => searchParams.get('group') || ''); // '' = alle, 'none' = ohne Gruppe, ID = spezifische Gruppe
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  
  // Bulk Assign Dialog State
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [customerGroups, setCustomerGroups] = useState<{ id: string; name: string }[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [bulkAssignMode, setBulkAssignMode] = useState<'assign_if_empty' | 'overwrite'>('assign_if_empty');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssignResult, setBulkAssignResult] = useState<{ added: number; alreadyInGroup: number; skippedHasOtherGroup: number; failed: number } | null>(null);
  
  // Inline Create Group State
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  

  // Load customers with optional server-side filter
  const loadCustomers = useCallback(async (prefix?: string, withoutGroup?: boolean) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (prefix) params.set('customerNoPrefix', prefix);
      if (withoutGroup) params.set('withoutGroup', 'true');
      // Deaktivierte immer mitladen; welche davon sichtbar sind, entscheidet
      // der Statusfilter unten. Ohne sie wären deaktivierte Kunden weder
      // auffindbar noch wieder zu aktivieren.
      params.set('includeInactive', 'true');

      const url = `/api/admin/customers${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
        // Clear selection when filter changes
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
    }
    setLoading(false);
  }, []);
  
  // Load customer groups for bulk assign
  // GUARDRAIL: Nur COLLECTIVE/RESIDENT laden, SYSTEM_BUCKET ausschließen
  const loadCustomerGroups = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/customer-groups', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Phase 1 Guardrail: SYSTEM_BUCKET darf NICHT in Kunden-Zuordnung erscheinen
        setCustomerGroups(
          data.filter((g: any) => g.isActive && g.type !== 'SYSTEM_BUCKET')
        );
      }
    } catch (err) {
      console.error('Fehler beim Laden der Kundengruppen:', err);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadCustomerGroups();
  }, [loadCustomers, loadCustomerGroups]);
  
  // Apply filter with debounce for prefix input
  useEffect(() => {
    const timer = setTimeout(() => {
      loadCustomers(
        customerNoPrefix || undefined, 
        filterGroupId === 'none' ? true : false
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [customerNoPrefix, filterGroupId, loadCustomers]);

  // Client-side filter (Name, Kürzel, Extern-ID, Gruppe) - on top of server-side prefix filter
  const filtered = useMemo(() => {
    let result = customers;

    // Status: standardmäßig nur aktive — deaktivierte auf Wunsch sichtbar
    if (statusFilter === 'active') {
      result = result.filter(c => c.isActive !== false);
    } else if (statusFilter === 'inactive') {
      result = result.filter(c => c.isActive === false);
    }

    // Gruppen-Filter (client-side für spezifische Gruppe)
    if (filterGroupId && filterGroupId !== 'none') {
      result = result.filter(c => c.customerGroupId === filterGroupId);
    }
    
    // Suche
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(c => 
        c.name.toLowerCase().includes(s) ||
        c.shortName?.toLowerCase().includes(s) ||
        c.externalDeliveryId?.toLowerCase().includes(s) ||
        c.externalCustomerNo?.toLowerCase().includes(s) ||
        c.customerGroup?.name.toLowerCase().includes(s)
      );
    }
    return result;
  }, [customers, search, filterGroupId, statusFilter]);
  
  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  const selectAll = () => {
    setSelectedIds(new Set(filtered.map(c => c.id)));
  };
  
  const deselectAll = () => {
    setSelectedIds(new Set());
  };
  
  const isAllSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));
  const selectedCount = selectedIds.size;
  
  // Check if all selected customers have no group (for mode simplification)
  const allSelectedWithoutGroup = useMemo(() => {
    const selectedCustomers = customers.filter(c => selectedIds.has(c.id));
    return selectedCustomers.length > 0 && selectedCustomers.every(c => !c.customerGroupId);
  }, [customers, selectedIds]);
  
  // Inline create group handler
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    
    setCreatingGroup(true);
    try {
      const res = await fetch('/api/admin/customer-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newGroupName.trim(),
          code: newGroupName.trim().toUpperCase().replace(/[^A-Z0-9]/g, '_').slice(0, 20),
          type: 'COLLECTIVE',
        }),
      });
      
      if (res.ok) {
        const created = await res.json();
        // Reload groups and select the new one
        await loadCustomerGroups();
        setSelectedGroupId(created.id);
        setShowInlineCreate(false);
        setNewGroupName('');
      }
    } catch (err) {
      console.error('Fehler beim Anlegen der Gruppe:', err);
    }
    setCreatingGroup(false);
  };
  
  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (!selectedGroupId || selectedIds.size === 0) return;
    
    setBulkAssigning(true);
    setBulkAssignResult(null);
    
    try {
      const res = await fetch(`/api/admin/customer-groups/${selectedGroupId}/customers/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customerIds: Array.from(selectedIds),
          mode: bulkAssignMode,
        }),
      });
      
      if (res.ok) {
        const result = await res.json();
        setBulkAssignResult(result);
        // Reload customers to show updated group assignments
        loadCustomers(customerNoPrefix || undefined, filterGroupId === 'none');
        // Clear selection after successful assign
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error('Fehler bei Bulk-Zuordnung:', err);
    }
    
    setBulkAssigning(false);
  };


  // Navigation to detail page
  const openCustomer = (customer?: Customer) => {
    if (customer) {
      navigate(`/admin/customers/${customer.id}`);
    } else {
      navigate('/admin/customers/new');
    }
  };

  // Remove customer from group (single)
  const removeFromGroup = async (customerId: string) => {
    if (!filterGroupId || filterGroupId === 'none') return;
    
    setRemovingFromGroup(customerId);
    try {
      const res = await fetch(`/api/admin/customer-groups/${filterGroupId}/customers`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customerIds: [customerId] }),
      });
      
      if (res.ok) {
        loadCustomers(customerNoPrefix || undefined, filterGroupId === 'none');
      }
    } catch (err) {
      console.error('Fehler beim Entfernen aus Gruppe:', err);
    }
    setRemovingFromGroup(null);
  };

  // Bulk remove from group
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const bulkRemoveFromGroup = async () => {
    if (!filterGroupId || filterGroupId === 'none' || selectedIds.size === 0) return;
    
    setBulkRemoving(true);
    try {
      const res = await fetch(`/api/admin/customer-groups/${filterGroupId}/customers`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customerIds: Array.from(selectedIds) }),
      });
      
      if (res.ok) {
        loadCustomers(customerNoPrefix || undefined, filterGroupId === 'none');
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error('Fehler beim Bulk-Entfernen aus Gruppe:', err);
    }
    setBulkRemoving(false);
  };

  // Import fields definition
  const importFields: ImportField[] = [
    { key: 'externalDeliveryId', label: 'Lieferadr.-Nr.', required: true, description: 'Eindeutige Lieferadress-Nummer (Upsert-Key)' },
    { key: 'externalCustomerNo', label: 'Kundennr.', required: true, description: 'ERP-Kundennummer (Gruppierung)' },
    { key: 'name', label: 'Name', required: true, description: 'Kundenname oder Ferienwohnungsname' },
  ];

  // Import handler with dryRun and applyUpdates support
  const handleImport = async (
    data: Record<string, unknown>[], 
    mapping: FieldMapping,
    options?: { dryRun?: boolean; applyUpdates?: boolean }
  ): Promise<ImportResult> => {
    const res = await fetch('/api/admin/customers/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data, mapping, options }),
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Import fehlgeschlagen' }));
      throw new Error(err.error || 'Import fehlgeschlagen');
    }
    
    const result = await res.json();
    
    // Nur bei echtem Import (nicht dryRun) Result setzen und Customers neu laden
    if (!options?.dryRun) {
      setImportResult(result);
      loadCustomers();
    }
    
    return result;
  };

  // Check if user can import (ADMIN/SUPERADMIN/SYSTEM)
  const canImport = currentUser && ['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(currentUser.role);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Kunden
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} von {customers.filter(c => c.isActive !== false).length} Kunden
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadCustomers()}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {canImport && (
              <button 
                onClick={() => { setImportResult(null); setImportDialogOpen(true); }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
            )}
            <button 
              onClick={() => openCustomer()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neuer Kunde
            </button>
          </div>
        </div>

        {/* Filter Bar - Live-Filter ohne Button */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Suche */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Gruppe Filter */}
          <select
            value={filterGroupId}
            onChange={(e) => setFilterGroupId(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[160px]"
          >
            <option value="">Alle Gruppen</option>
            <option value="none">Ohne Gruppe</option>
            {customerGroups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          
          {/* Kundennr. Prefix */}
          <input
            type="text"
            placeholder="Kundennr. 282..."
            value={customerNoPrefix}
            onChange={(e) => setCustomerNoPrefix(e.target.value)}
            className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          {/* Status — wie in der Benutzerverwaltung */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | 'all')}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            aria-label="Status"
          >
            <option value="active">Aktiv</option>
            <option value="inactive">Inaktiv</option>
            <option value="all">Alle</option>
          </select>
        </div>
        
        {/* Bulk Actions Bar */}
        {selectedCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-blue-800">
              <strong>{selectedCount}</strong> Kunde{selectedCount !== 1 ? 'n' : ''} ausgewählt
            </span>
            <div className="flex gap-2">
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-sm border border-blue-300 rounded hover:bg-blue-100"
              >
                Auswahl aufheben
              </button>
              <button
                onClick={() => { setBulkAssignResult(null); setBulkAssignDialogOpen(true); }}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
              >
                <Users className="w-4 h-4" />
                Zur Gruppe hinzufügen
              </button>
              {filterGroupId && filterGroupId !== 'none' && (
                <button
                  onClick={bulkRemoveFromGroup}
                  disabled={bulkRemoving}
                  className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <UserMinus className="w-4 h-4" />
                  {bulkRemoving ? 'Entferne...' : 'Aus Gruppe entfernen'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search 
                ? 'Keine Treffer' 
                : filterGroupId && filterGroupId !== 'none'
                  ? 'Keine Kunden in dieser Gruppe'
                  : filterGroupId === 'none'
                    ? 'Keine Kunden ohne Gruppe'
                    : 'Keine Kunden vorhanden'}
              {!search && !filterGroupId && customers.length === 0 && (
                <button 
                  onClick={() => openCustomer()}
                  className="block mx-auto mt-4 text-blue-600 hover:underline"
                >
                  Ersten Kunden anlegen
                </button>
              )}
            </div>
          ) : (
            /* Normale Tabellenansicht */
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={() => isAllSelected ? deselectAll() : selectAll()}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      title={isAllSelected ? 'Alle abwählen' : `Alle ${filtered.length} auswählen`}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Kunde</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Typ</th>
                  {filterGroupId && filterGroupId !== 'none' && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-20"></th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(c => (
                  <tr 
                    key={c.id} 
                    className={`hover:bg-slate-50 cursor-pointer ${selectedIds.has(c.id) ? 'bg-blue-50' : ''}`}
                    onClick={() => openCustomer(c)}
                  >
                    <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    {/* Name + Subline + Gruppen-Badge */}
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium truncate ${c.isActive === false ? 'text-slate-400' : 'text-slate-900'}`}>
                              {c.name}
                            </span>
                            {/* Ohne Kennzeichnung wäre in der Ansicht "Alle" nicht
                                erkennbar, welche Kunden nicht mehr erfassbar sind. */}
                            {c.isActive === false && (
                              <span className="shrink-0 px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                                deaktiviert
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {c.externalCustomerNo && `Kd. ${c.externalCustomerNo}`}
                            {c.externalCustomerNo && c.externalDeliveryId && ' · '}
                            {c.externalDeliveryId && `Lief. ${c.externalDeliveryId}`}
                          </div>
                        </div>
                        {/* Gruppen-Badge - nur anzeigen wenn Gruppe vorhanden */}
                        {c.customerGroup && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterGroupId(c.customerGroup!.id);
                            }}
                            className="shrink-0 px-2 py-0.5 text-xs rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 transition-colors flex items-center gap-1"
                            title={`Filter: ${c.customerGroup.name}`}
                          >
                            <Users className="w-3 h-3" />
                            {c.customerGroup.name}
                          </button>
                        )}
                      </div>
                    </td>
                    {/* Typ Badge */}
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-600">{TYPE_LABELS[c.type]}</span>
                    </td>
                    {/* Aktion: Aus Gruppe entfernen (nur bei Gruppenfilter) */}
                    {filterGroupId && filterGroupId !== 'none' && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFromGroup(c.id); }}
                          disabled={removingFromGroup === c.id}
                          className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded disabled:opacity-50"
                          title={`Aus Gruppe „${customerGroups.find(g => g.id === filterGroupId)?.name}" entfernen`}
                        >
                          <UserMinus className={`w-4 h-4 ${removingFromGroup === c.id ? 'animate-pulse' : ''}`} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Import Dialog */}
      {importDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Kunden importieren</h2>
              <button
                onClick={() => setImportDialogOpen(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <CsvDropzone
                fields={importFields}
                onImport={handleImport}
                onClose={() => setImportDialogOpen(false)}
                title=""
                description="CSV oder Excel-Datei (.xlsx) mit Kundendaten hochladen. Lieferadr.-Nr. ist der eindeutige Schlüssel."
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Dialog */}
      {bulkAssignDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Zur Gruppe hinzufügen
              </h2>
              <button 
                onClick={() => setBulkAssignDialogOpen(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {bulkAssignResult ? (
                /* Ergebnis-Ansicht */
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium">Zuordnung abgeschlossen</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="text-slate-500">Hinzugefügt:</span>
                      <span className="font-medium ml-2">{bulkAssignResult.added}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="text-slate-500">Bereits in Gruppe:</span>
                      <span className="font-medium ml-2">{bulkAssignResult.alreadyInGroup}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="text-slate-500">Übersprungen (andere Gruppe):</span>
                      <span className="font-medium ml-2">{bulkAssignResult.skippedHasOtherGroup}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <span className="text-slate-500">Fehlgeschlagen:</span>
                      <span className="font-medium ml-2">{bulkAssignResult.failed}</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Formular-Ansicht */
                <>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <strong>{selectedCount}</strong> Kunde{selectedCount !== 1 ? 'n' : ''} werden der Gruppe zugeordnet
                  </div>
                  
                  {/* Zielgruppe mit Inline Create */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Zielgruppe <span className="text-red-500">*</span>
                    </label>
                    
                    {showInlineCreate ? (
                      /* Inline Create Form */
                      <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg space-y-3">
                        <input
                          type="text"
                          value={newGroupName}
                          onChange={(e) => setNewGroupName(e.target.value)}
                          placeholder="Name der neuen Gruppe"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setShowInlineCreate(false); setNewGroupName(''); }}
                            className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-white"
                          >
                            Abbrechen
                          </button>
                          <button
                            onClick={handleCreateGroup}
                            disabled={creatingGroup || !newGroupName.trim()}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {creatingGroup ? 'Anlegen...' : 'Anlegen'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Dropdown mit Create Option */
                      <div className="space-y-2">
                        <select
                          value={selectedGroupId}
                          onChange={(e) => {
                            if (e.target.value === '__create__') {
                              setShowInlineCreate(true);
                            } else {
                              setSelectedGroupId(e.target.value);
                            }
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Gruppe wählen...</option>
                          {customerGroups.map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                          <option value="__create__">+ Neue Gruppe anlegen</option>
                        </select>
                      </div>
                    )}
                  </div>
                  
                  {/* Modus - nur anzeigen wenn nicht alle ohne Gruppe */}
                  {!allSelectedWithoutGroup && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Was soll mit bestehenden Gruppenzuordnungen passieren?
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer p-3 border rounded-lg hover:bg-slate-50">
                          <input
                            type="radio"
                            name="bulkMode"
                            checked={bulkAssignMode === 'assign_if_empty'}
                            onChange={() => setBulkAssignMode('assign_if_empty')}
                            className="mt-0.5"
                          />
                          <div>
                            <span className="text-sm">Kunden ohne Gruppe zur Zielgruppe hinzufügen</span>
                            <p className="text-xs text-slate-500 mt-0.5">Bereits zugeordnete Kunden bleiben unverändert</p>
                          </div>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer p-3 border rounded-lg hover:bg-slate-50">
                          <input
                            type="radio"
                            name="bulkMode"
                            checked={bulkAssignMode === 'overwrite'}
                            onChange={() => setBulkAssignMode('overwrite')}
                            className="mt-0.5"
                          />
                          <div>
                            <span className="text-sm">Bestehende Gruppenzuordnung ersetzen</span>
                            <p className="text-xs text-slate-500 mt-0.5">Alle ausgewählten Kunden werden der Zielgruppe zugeordnet</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setBulkAssignDialogOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                {bulkAssignResult ? 'Schließen' : 'Abbrechen'}
              </button>
              {!bulkAssignResult && (
                <button
                  onClick={handleBulkAssign}
                  disabled={bulkAssigning || !selectedGroupId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkAssigning ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Zuordnen...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Zuordnen
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
